import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { parseMarkdownPreview, importFromMarkdown } from '../../services/person-api.js';
import type { ParsedPersonMarkdown, PersonWithDetails } from '../../types/person.js';

@customElement('cp-person-import-modal')
export class CpPersonImportModal extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _step: 'input' | 'preview' = 'input';
  @state() private _markdown = '';
  @state() private _parsed: ParsedPersonMarkdown | null = null;
  @state() private _loading = false;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  open(): void {
    this._markdown = '';
    this._parsed = null;
    this._step = 'input';
    this._loading = false;
    this._error = '';
    this._open = true;
  }

  private _close(): void {
    this._open = false;
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) this._close();
  };

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-person-import__overlay')) {
      this._close();
    }
  }

  private async _onPreview(): Promise<void> {
    if (!this._markdown.trim()) {
      this._error = 'Bitte Markdown-Text eingeben';
      return;
    }

    this._loading = true;
    this._error = '';

    try {
      this._parsed = await parseMarkdownPreview(this._markdown);
      if (!this._parsed.full_name) {
        this._error = 'Kein Name im Markdown gefunden. Bitte mit "# Name" beginnen.';
        this._loading = false;
        return;
      }
      this._step = 'preview';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Vorschau konnte nicht erstellt werden';
    } finally {
      this._loading = false;
    }
  }

  private _onBack(): void {
    this._step = 'input';
    this._error = '';
  }

  private async _onImport(): Promise<void> {
    this._loading = true;
    this._error = '';

    try {
      const person = await importFromMarkdown(this._markdown);
      this._open = false;
      this.dispatchEvent(new CustomEvent<{ person: PersonWithDetails }>('person-created', {
        detail: { person },
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Import fehlgeschlagen';
    } finally {
      this._loading = false;
    }
  }

  render() {
    if (!this._open) return nothing;

    return html`
      <div class="cp-person-import__overlay" @click=${this._onBackdropClick}>
        <div class="cp-person-import__dialog">
          <div class="cp-person-import__header">
            <h2 class="cp-person-import__title">
              ${this._step === 'input' ? 'Markdown importieren' : 'Vorschau'}
            </h2>
            <button class="cp-person-import__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-person-import__body">
            ${this._step === 'input' ? this._renderInput() : this._renderPreview()}

            ${this._error ? html`
              <div class="cp-person-import__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-person-import__footer">
            ${this._step === 'input' ? html`
              <button
                class="cp-person-import__btn cp-person-import__btn--cancel"
                @click=${this._close}
                ?disabled=${this._loading}
              >Abbrechen</button>
              <button
                class="cp-person-import__btn cp-person-import__btn--submit"
                @click=${this._onPreview}
                ?disabled=${this._loading || !this._markdown.trim()}
              >${this._loading ? 'Wird analysiert...' : 'Vorschau'}</button>
            ` : html`
              <button
                class="cp-person-import__btn cp-person-import__btn--cancel"
                @click=${this._onBack}
                ?disabled=${this._loading}
              >Zurueck</button>
              <button
                class="cp-person-import__btn cp-person-import__btn--submit"
                @click=${this._onImport}
                ?disabled=${this._loading}
              >${this._loading ? 'Wird importiert...' : 'Importieren'}</button>
            `}
          </div>
        </div>
      </div>
    `;
  }

  private _renderInput() {
    return html`
      <textarea
        class="cp-person-import__textarea"
        placeholder="# Max Mustermann${'\n'}Kategorie: Geschaeftlich${'\n'}E-Mail: max@example.com${'\n'}Telefon: +49 123 456${'\n'}Geburtstag: 15.03.1985${'\n'}Tags: kollege, muenchen${'\n'}${'\n'}## Berufliches${'\n'}Arbeitet als Entwickler...${'\n'}${'\n'}## Privates${'\n'}Hobbys und Interessen..."
        .value=${this._markdown}
        @input=${(e: Event) => { this._markdown = (e.target as HTMLTextAreaElement).value; }}
        ?disabled=${this._loading}
      ></textarea>
    `;
  }

  private _renderPreview() {
    if (!this._parsed) return nothing;
    const p = this._parsed;

    const metaFields: Array<{ label: string; value: string | undefined }> = [
      { label: 'Name', value: p.full_name },
      { label: 'Rufname', value: p.nickname },
      { label: 'Kategorie', value: p.category },
      { label: 'Beziehungstyp', value: p.relationship_type },
      { label: 'E-Mail', value: p.email },
      { label: 'Telefon', value: p.phone },
      { label: 'Geburtstag', value: p.birthday },
      { label: 'Subtitle', value: p.subtitle },
    ];

    return html`
      <div class="cp-person-import__preview">
        <div class="cp-person-import__meta">
          ${metaFields.filter(f => f.value).map(f => html`
            <div class="cp-person-import__meta-row">
              <span class="cp-person-import__meta-label">${f.label}</span>
              <span class="cp-person-import__meta-value">${f.value}</span>
            </div>
          `)}
        </div>

        ${p.tags.length > 0 ? html`
          <div class="cp-person-import__tags">
            ${p.tags.map(t => html`<span class="cp-person-import__tag">${t}</span>`)}
          </div>
        ` : nothing}

        ${p.sections.length > 0 ? html`
          <div class="cp-person-import__sections">
            <h3 class="cp-person-import__sections-title">Sektionen (${p.sections.length})</h3>
            ${p.sections.map(s => html`
              <details class="cp-person-import__section">
                <summary class="cp-person-import__section-title">${s.title}</summary>
                <div class="cp-person-import__section-content">${s.content}</div>
              </details>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-person-import-modal': CpPersonImportModal;
  }
}
